import BucketedFacetSerializer from './serializers/BucketedFacetSerializer'
import NumericRangeFacetSerializer from './serializers/NumericRangeFacetSerializer'
import {
  FacetSelection,
  FacetSelectionPayloadFragment,
  DisplayFacetFragment,
  NumericRangeDisplayFacetFragment,
  SellableSearchFilter
} from '#src/generated/graphql'
import {
  FacetSerializer,
  SearchResultEdge,
  SearchResultConnection,
  SearchResultNode
} from './types'
import numeral from 'numeral'
import uniq from 'lodash/uniq'
import without from 'lodash/without'
import cloneDeep from 'lodash/cloneDeep'
import fromPairs from 'lodash/fromPairs'
import repeat from 'lodash/repeat'
import chunk from 'lodash/chunk'
import trim from 'lodash/trim'
import omit from 'lodash/omit'
import logger from '../logger'

const FACET_SERIALIZERS: FacetSerializer[] = [BucketedFacetSerializer, NumericRangeFacetSerializer]

export interface AliasedFacetSelection extends FacetSelection {
  alias: string | null
}

export interface SearchQuery {
  categorySlug: string
  keywords?: string
  // TODO: Switch from a dict to an array?
  selections?: {
    [facetName: string]: AliasedFacetSelection | FacetSelectionPayloadFragment
  }
  filters?: SellableSearchFilter[]
  after?: string
  before?: string
}

export interface SearchLocation {
  pathname: string
  searchParams: URLSearchParams
}

/**
 * Serializes a `SearchQuery` into a format that can be used as a URL:
 *
 *  - The category is serialized as the URL path
 *  - The keywords, search type, and pagination cursors are serialized as top-level query params
 *  - The facets are serialized as query params with prefixes denoting their facet type.
 *      (each facet type determines its own serialization format)
 */
export function serializeSearchQuery(query: SearchQuery): SearchLocation {
  if (!query.categorySlug) {
    throw new Error('Can not serialize search query without a category slug')
  }

  let pathname = '/c/' + query.categorySlug
  const pathSegments = []
  const searchParams = new URLSearchParams()

  if (query.keywords) {
    searchParams.append('q', query.keywords)
  }

  if (query.after) {
    searchParams.append('after', query.after)
  }

  if (query.before) {
    searchParams.append('before', query.before)
  }

  if (query.selections) {
    const sortedSelections = Object.values(query.selections).sort((a, b) => {
      const keyA = a.alias || a.name
      const keyB = b.alias || b.name

      // Sort by length of key, then lexigraphically
      if (keyA.length !== keyB.length) {
        return keyA.length - keyB.length
      } else {
        return keyA.localeCompare(keyB)
      }
    })

    for (let facetSelection of sortedSelections) {
      // For each selection, find the appropriate serializer for that facet selection type,
      //   based on the `isSerializable` type guard implementations.
      const serializer = FACET_SERIALIZERS.find(serializer => {
        return serializer.isSerializable(facetSelection)
      })

      if (!serializer) {
        throw new Error(
          `Could not determine the facet selection type for the "${facetSelection.name}" facet`
        )
      }

      const name = facetSelection.name
      const alias = facetSelection.alias
      const value = serializer.serialize(facetSelection)
      const prefix = serializer.SERIALIZATION_PREFIX

      if (value) {
        const selectionCount = facetSelection.bucketValues ? facetSelection.bucketValues.length : 0

        if (alias && selectionCount === 1) {
          pathSegments.push(`${alias}/${encodeURIComponent(value)}`)
        } else {
          searchParams.append(`${prefix}:${alias || name}`, value)
        }
      }
    }
  }

  if (query.filters) {
    let filters = query.filters.slice()

    // Special case: winter sports deals
    const isWinterSportsDealsSearch =
      query.categorySlug === 'winter-sports' &&
      filters.includes(SellableSearchFilter.DISCOUNTED) &&
      filters.includes(SellableSearchFilter.SHIPPED_BY_CURATED)

    // Special case: other product department deals
    const isProductsDealsSearch =
      query.categorySlug !== 'yacht-charters' && filters.includes(SellableSearchFilter.DISCOUNTED)

    if (isWinterSportsDealsSearch) {
      // Remove the "DISCOUNTED" and "SHIPPED_BY_CURATED" as both filters are implied by the
      //   "deals" suffix when browsing "winter sports"
      filters = without(
        filters,
        SellableSearchFilter.DISCOUNTED,
        SellableSearchFilter.SHIPPED_BY_CURATED
      )
      pathSegments.push('deals')
    } else if (isProductsDealsSearch) {
      // Remove the "DISCOUNTED" as that filter is implied by the "deals" suffix
      filters = without(filters, SellableSearchFilter.DISCOUNTED)
      pathSegments.push('deals')
    }

    // Encode any remaining filters as the `filters` query param
    if (filters.length) {
      searchParams.append('filters', filters.map(filter => filter.toLowerCase()).join(','))
    }
  }

  if (pathSegments.length) {
    pathname += '/' + pathSegments.join('/')
  }

  return {
    pathname,
    searchParams
  }
}

/**
 * Deserialize a URL (path and query string) to a `SearchQuery`
 *
 * See the comment for `serializeSearchQuery` for details on the serialization format.
 */
export function deserializeSearchQuery({ pathname, searchParams }: SearchLocation): SearchQuery {
  // Deserialize the path portion of the URL:
  //  1) Remove the special "/deals" suffix
  //  2) Trim leading and trailing forward slashes    => key-1/val-1/key-2/val-2
  //  3) Split by remaining forward slashes           => [key-1, val-1, key-2, val-2]
  //  4) Chunk it into groups of 2                    => [[key-1, val-1], [key-2, val-2]]
  //  5) Convert it to a dictionary                   => { key-1: val-1, key-2: val-2}
  const isDealsSearch = pathname.endsWith('/deals')
  pathname = pathname.replace(/\/deals$/, '')
  const deserializedPathSegments = fromPairs(chunk(trim(pathname, '/').split('/'), 2))
  const categorySlug = deserializedPathSegments['c']
  const facetSelectionKV = omit(deserializedPathSegments, 'c')

  const query: SearchQuery = {
    categorySlug
  }

  for (let [facetAlias, facetValue] of Object.entries(facetSelectionKV)) {
    if (!query.selections) {
      query.selections = {}
    }

    query.selections[facetAlias] = {
      name: facetAlias,
      alias: facetAlias,
      bucketValues: [decodeURIComponent(facetValue)]
    }
  }

  // Special case: winter sports deals
  if (isDealsSearch && categorySlug === 'winter-sports') {
    query.filters = [SellableSearchFilter.DISCOUNTED, SellableSearchFilter.SHIPPED_BY_CURATED]
  }
  // Special case: other product department deals
  else if (isDealsSearch && categorySlug !== 'yacht-charters') {
    query.filters = [SellableSearchFilter.DISCOUNTED]
  }

  // @ts-ignore TypeScript has incomplete typings for URLSearchParams
  // See: https://github.com/Microsoft/TSJS-lib-generator/pull/167#issuecomment-263764497
  for (var [key, value] of searchParams.entries()) {
    switch (key) {
      case 'q':
        query.keywords = value
        break
      case 'after':
        query.after = value
        break
      case 'before':
        query.before = value
        break
      case 'filters':
        query.filters = query.filters || []

        value.split(',').forEach((filterValue: string) => {
          const filterEnumValue = filterValue.toUpperCase()

          if (filterEnumValue in SellableSearchFilter) {
            query.filters?.push(
              SellableSearchFilter[(filterEnumValue as unknown) as SellableSearchFilter]
            )
          } else {
            logger.captureException(`Unknown filter in search URL: ${filterEnumValue}`)
          }
        })
        break
      default:
        const prefixedQueryParam: string = key

        // For each selection, find the appropriate serializer for that facet selection type
        //   based on the facet's prefix.
        FACET_SERIALIZERS.some(serializer => {
          const prefix = serializer.SERIALIZATION_PREFIX

          if (prefixedQueryParam.startsWith(prefix + ':')) {
            if (!query.selections) {
              query.selections = {}
            }

            const facetName = prefixedQueryParam.slice(prefix.length + 1)

            query.selections[facetName] = {
              name: facetName,
              alias: null,
              ...serializer.deserialize(value)
            }

            return true
          }

          return false
        })
    }
  }

  query.filters = uniq(query.filters)

  return query
}

/**
 * "Select" a bucket value for a bucketed facet given an existing SearchQuery
 */
export function addBucketSelectionToSearchQuery(
  existingQuery: SearchQuery,
  displayFacet: DisplayFacetFragment,
  value: string
) {
  const newQuery = cloneDeep(existingQuery)
  const existingSelection = cloneDeep(existingQuery.selections || {})[
    displayFacet.alias || displayFacet.name
  ]
  const existingBuckets = new Set(existingSelection ? existingSelection.bucketValues : [])

  existingBuckets.add(value)

  if (!newQuery.selections) {
    newQuery.selections = {}
  }

  newQuery.selections[displayFacet.alias || displayFacet.name] = {
    name: displayFacet.name,
    alias: displayFacet.alias,
    bucketValues: Array.from(existingBuckets)
  } as AliasedFacetSelection

  return newQuery
}

/**
 * Safely convert a SearchQuery into an array of FacetSelections, suitable to use as GraphQL input
 */
export function searchQueryToFacetSelections(query: SearchQuery): FacetSelection[] {
  return Object.values(cloneDeep(query.selections || {})).map(selection => {
    delete selection.alias
    return selection
  })
}

/**
 * Merge an array of newly fetched edges into an array of existing edges
 */
export function mergeEdges<TEdge extends SearchResultEdge<any>>(
  previousEdges: TEdge[],
  newEdges: TEdge[]
) {
  const existingIds = new Set(previousEdges.map(edge => edge.node.id))
  const uniqueNewEdges = newEdges.filter(edge => !existingIds.has(edge.node.id))

  return [...previousEdges, ...uniqueNewEdges]
}

/**
 * Merge a newly fetched connection into an existing connection
 */
export function mergeConnections<
  TConnectionType = any,
  TEdge extends SearchResultEdge<TNode> = any,
  TNode extends SearchResultNode = any
>(
  previousConnection: SearchResultConnection<TConnectionType, TEdge, TNode>,
  newConnection: SearchResultConnection<TConnectionType, TEdge, TNode>
) {
  return {
    __typename: newConnection.__typename,
    count: newConnection.count,
    facets: newConnection.facets,
    edges: mergeEdges(previousConnection.edges, newConnection.edges),
    pageInfo: newConnection.pageInfo
  }
}

/**
 * Format a single selection value for a NumericRangeDisplayFacet in a format suitable
 *   for use as a label (i.e. abbreviated)
 */
export function formatNumericFacetValueAsLabel(
  value: number,
  facet: NumericRangeDisplayFacetFragment
) {
  const scale = facet.scale
  const upper = parseInt(facet.upper, 10)
  const isAboveUpperCap = value >= upper && facet.upperCapped
  const suffix = isAboveUpperCap ? '+' : ''

  switch (facet.type) {
    case 'USD_CURRENCY':
      // Use abbreviations for values of more than $10,000
      return numeral(value / 100).format(value / 100 >= 10000 ? '($0a)' : '$0,0') + suffix

    default:
      // Scale the stored to it's actual representation, and include that many decimals
      const format = scale > 0 ? `0,0.${repeat('0', scale)}` : '0,0'
      return numeral(value * Math.pow(10, -scale)).format(format) + suffix
  }
}

/**
 * Format a FacetSelection using `formatNumericFacetValueAsLabel`
 */
export function formatNumericFacetSelectionAsLabel(
  selection: FacetSelection,
  facet: NumericRangeDisplayFacetFragment
) {
  const lowerLabel = selection.lower && formatNumericFacetValueAsLabel(selection.lower, facet)
  const upperLabel = selection.upper && formatNumericFacetValueAsLabel(selection.upper, facet)

  if (lowerLabel && upperLabel) {
    return `${lowerLabel} - ${upperLabel}`
  } else if (lowerLabel) {
    return `${lowerLabel}+`
  } else if (upperLabel) {
    return `Up to ${upperLabel}`
  } else {
    return 'Any'
  }
}
